/* The API client.

   It is responsible for exactly five things, and nothing else belongs here:

     1. Attaching the access token.
     2. Generating and attaching X-Trace-Id on every request.
     3. Attaching X-Build-Id so log correlation can spot version mismatches.
     4. Normalising the error envelope into a typed ApiError.
     5. Refresh-on-401 with request queueing.

   Retry policy lives in TanStack Query, except for the exam sync, which owns
   its own (see features/exam/hooks/useAnswerSync.ts). */

import createClient, { type Middleware } from 'openapi-fetch'
import type { paths, components } from './schema'
import { newTraceId, rememberTraceId } from '@/lib/trace'

type ErrorBody = components['schemas']['ErrorBody']
type ErrorDetail = components['schemas']['ErrorDetail']

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'
const BUILD_ID = import.meta.env.VITE_BUILD_ID ?? 'local'

/* ── The typed error ─────────────────────────────────────────────────────
   Callers switch on `code`. They must never parse `message`. */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly traceId: string
  readonly details: ErrorDetail[]

  /* How long the server said to wait, from `Retry-After`. Null when it said
     nothing — which is the honest answer, and the UI must not invent one. */
  readonly retryAfterSeconds: number | null

  /* The same fact as a wall-clock instant. Seconds pass between the response
     arriving and a countdown being rendered — sometimes many, if the error sits
     on screen — so a deadline stays true where a duration silently rots. */
  readonly retryAt: number | null

  constructor(init: {
    code: string
    message: string
    status: number
    traceId: string
    details?: ErrorDetail[]
    retryAfterSeconds?: number | null
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.code = init.code
    this.status = init.status
    this.traceId = init.traceId
    this.details = init.details ?? []
    this.retryAfterSeconds = init.retryAfterSeconds ?? null
    this.retryAt =
      this.retryAfterSeconds === null ? null : Date.now() + this.retryAfterSeconds * 1000
  }

  /** Network failure, DNS, or a request the browser never completed. */
  get isNetwork(): boolean {
    return this.status === 0
  }

  get isServer(): boolean {
    return this.status >= 500
  }

  /** Retrying a 4xx (other than 429) will produce the same 4xx. */
  get isRetryable(): boolean {
    return this.isNetwork || this.isServer || this.status === 429
  }

  /** Validation errors map onto form fields by `details[].field`. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const detail of this.details) {
      // `field` is optional on the wire: a detail without one is a
      // form-level message, not a field-level one.
      if (detail.field != null) out[detail.field] = detail.message
    }
    return out
  }
}

/* Stable codes the UI switches on. Keeping them in one place means a typo is a
   compile error rather than a branch that silently never fires. */
export const ErrorCode = {
  ENROLLMENT_EXPIRED: 'ENROLLMENT_EXPIRED',
  MAX_ATTEMPTS_REACHED: 'MAX_ATTEMPTS_REACHED',
  ATTEMPT_NOT_ACTIVE: 'ATTEMPT_NOT_ACTIVE',
  TEST_WINDOW_CLOSED: 'TEST_WINDOW_CLOSED',
  TEST_HAS_ATTEMPTS: 'TEST_HAS_ATTEMPTS',
  /* Publishing a second language. These two must never be treated alike:
     a count mismatch cannot be overridden, a key mismatch can. */
  MEDIUM_COUNT_MISMATCH: 'MEDIUM_COUNT_MISMATCH',
  MEDIUM_KEY_MISMATCH: 'MEDIUM_KEY_MISMATCH',
  /* Share-link feedback. Both are defence-in-depth rather than user-facing:
     the form is only rendered for a guest on a submitted attempt, so either
     firing means the client and the server disagree about what this attempt
     is — worth naming so that shows up in telemetry as itself. */
  FEEDBACK_NOT_APPLICABLE: 'FEEDBACK_NOT_APPLICABLE',
  ATTEMPT_NOT_SUBMITTED: 'ATTEMPT_NOT_SUBMITTED',
  SESSION_REPLACED: 'SESSION_REPLACED',
  RATE_LIMITED: 'RATE_LIMITED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  NETWORK: 'NETWORK',
  UNKNOWN: 'UNKNOWN',
} as const

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode]

/* ── 1. Access token — memory only ───────────────────────────────────────
   Never localStorage: that is an XSS exfiltration target. Losing it on a
   reload is fine, because the httpOnly refresh cookie restores the session.

   A GUEST token is the one exception, and it is not stored here — see
   features/exam/guestSession.ts for what is persisted and why it has to be.
   What matters at this layer is that a guest token is NOT refreshable: there
   is no credential behind it to re-present, so the backend issues no refresh
   cookie for one. Attempting a refresh on its 401 would burn a round trip and
   then report a generic "session lost" for what is really an expired exam. */
type TokenKind = 'account' | 'guest'

let accessToken: string | null = null
let tokenKind: TokenKind = 'account'
let onSessionLost: (() => void) | null = null

export function setAccessToken(token: string | null, kind: TokenKind = 'account'): void {
  accessToken = token
  tokenKind = kind
}

/** Whether the current token can be renewed. False for a guest. */
export function isRefreshableSession(): boolean {
  return tokenKind === 'account'
}

export function getAccessToken(): string | null {
  return accessToken
}

/** Called when refresh fails terminally — the app shows a re-login prompt. */
export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler
}

/* ── 5. Refresh-on-401 with request queueing ─────────────────────────────
   Concurrent 401s queue behind ONE in-flight refresh. With refresh rotation on
   the backend, parallel refreshes revoke each other's tokens and log the
   student out — during an exam. */
let refreshInFlight: Promise<string | null> | null = null

async function performRefresh(): Promise<string | null> {
  try {
    const res = await fetch(`${BASE_URL}/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Trace-Id': newTraceId(),
        'X-Build-Id': BUILD_ID,
      },
    })
    if (!res.ok) return null
    const body = (await res.json()) as components['schemas']['TokenResponse']
    setAccessToken(body.access_token)
    scheduleProactiveRefresh(body.expires_in)
    return body.access_token
  } catch {
    return null
  }
}

export function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/* ── Proactive refresh ───────────────────────────────────────────────────
   At ~80% of token lifetime, not only reactively on a 401. During a 90-minute
   exam a reactive-only strategy produces a visible stall at the moment of
   expiry — exactly when the student is answering. */
let refreshTimer: ReturnType<typeof setTimeout> | null = null

export function scheduleProactiveRefresh(expiresInSeconds: number): void {
  cancelProactiveRefresh()
  const delayMs = Math.max(30_000, expiresInSeconds * 1000 * 0.8)
  refreshTimer = setTimeout(() => {
    void refreshAccessToken().then((token) => {
      if (token === null) onSessionLost?.()
    })
  }, delayMs)
}

export function cancelProactiveRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/* ── Middleware ──────────────────────────────────────────────────────────── */

const AUTH_FREE_PATHS = [
  /* Everything under /v1/public is anonymous by definition. Listing it here
     does two things: no token is attached, and a 401 from it can never kick
     off a refresh — so a signed-out visitor on the landing page never triggers
     the session-lost path. */
  '/v1/public/',
  '/v1/auth/login',
  '/v1/auth/admin/login',
  '/v1/auth/refresh',
  '/v1/auth/otp/request',
  '/v1/auth/otp/verify',
  '/v1/auth/password/set',
  '/v1/auth/password/forgot',
  '/v1/auth/password/reset',
  '/v1/health',
]

function isAuthFree(url: string): boolean {
  return AUTH_FREE_PATHS.some((path) => url.includes(path))
}

const middleware: Middleware = {
  async onRequest({ request }) {
    const traceId = newTraceId()
    rememberTraceId(traceId)

    // 2 & 3 — correlation headers on every request without exception.
    request.headers.set('X-Trace-Id', traceId)
    request.headers.set('X-Build-Id', BUILD_ID)

    // 1 — the access token.
    if (accessToken !== null && !isAuthFree(request.url)) {
      request.headers.set('Authorization', `Bearer ${accessToken}`)
    }
    return request
  },

  async onResponse({ request, response }) {
    if (response.status !== 401 || isAuthFree(request.url)) return response

    /* A guest has no refresh path at all. Their token is sized to outlive the
       exam window on its own, so a 401 means it is genuinely over — expired,
       or the link was revoked mid-exam. Either way there is nothing to renew
       and the app must say so rather than retry. */
    if (tokenKind === 'guest') {
      onSessionLost?.()
      return response
    }

    // 5 — one refresh, everyone else waits on it, then the original request
    // is replayed once. A second 401 after a fresh token is terminal.
    const token = await refreshAccessToken()
    if (token === null) {
      onSessionLost?.()
      return response
    }

    const retried = request.clone()
    retried.headers.set('Authorization', `Bearer ${token}`)
    retried.headers.set('X-Trace-Id', newTraceId())
    return fetch(retried)
  },
}

/* ── The client ──────────────────────────────────────────────────────────── */

export const api = createClient<paths>({
  baseUrl: BASE_URL,
  // The refresh cookie is httpOnly and must ride along on auth calls.
  credentials: 'include',
})

api.use(middleware)

/* ── 4. Error normalisation ──────────────────────────────────────────────
   Every endpoint wrapper funnels through `unwrap`, so no call site ever sees
   a raw response or has to remember the envelope shape. */

type FetchResult<T> = {
  data?: T
  error?: unknown
  response: Response
}

/* `Retry-After` is either a whole number of seconds or an HTTP-date. The spec
   allows both, so both are read: a limiter that switches representation must
   not silently turn a precise wait into "a moment". */
function parseRetryAfter(response: Response | null): number | null {
  const raw = response?.headers.get('Retry-After')
  if (raw === null || raw === undefined || raw.trim() === '') return null

  const seconds = Number(raw)
  if (Number.isFinite(seconds)) return Math.max(0, Math.round(seconds))

  const when = Date.parse(raw)
  if (Number.isNaN(when)) return null
  return Math.max(0, Math.round((when - Date.now()) / 1000))
}

export function toApiError(error: unknown, response: Response | null): ApiError {
  const status = response?.status ?? 0
  const headerTrace = response?.headers.get('X-Trace-Id') ?? ''
  /* Read for every status, not just 429: a 503 during a deploy carries one too,
     and the panel that shows the wait does not care which status produced it. */
  const retryAfterSeconds = parseRetryAfter(response)

  const envelope = (error as { error?: ErrorBody } | undefined)?.error
  if (envelope && typeof envelope.code === 'string') {
    return new ApiError({
      code: envelope.code,
      message: envelope.message,
      status,
      traceId: envelope.trace_id || headerTrace,
      details: envelope.details ?? undefined,
      retryAfterSeconds,
    })
  }

  if (status === 0) {
    return new ApiError({
      code: ErrorCode.NETWORK,
      message: 'We could not reach the server. Check your connection and try again.',
      status: 0,
      traceId: headerTrace,
    })
  }

  if (status === 401) {
    return new ApiError({
      code: ErrorCode.UNAUTHENTICATED,
      message: 'Your session has ended. Please sign in again.',
      status,
      traceId: headerTrace,
    })
  }

  if (status === 429) {
    return new ApiError({
      code: ErrorCode.RATE_LIMITED,
      /* No wait is quoted here even when one is known: the panel composes the
         timing sentence, and it must not end up said twice. */
      message: 'Too many requests.',
      status,
      traceId: headerTrace,
      retryAfterSeconds,
    })
  }

  return new ApiError({
    code: status >= 500 ? ErrorCode.UNKNOWN : ErrorCode.UNKNOWN,
    message:
      status >= 500
        ? 'Something went wrong on our side. Please try again.'
        : 'That request could not be completed.',
    status,
    traceId: headerTrace,
    retryAfterSeconds,
  })
}

/** Throws a typed ApiError, or returns the payload. */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>
  try {
    result = await promise
  } catch (cause) {
    // fetch itself rejected — offline, DNS, aborted.
    throw new ApiError({
      code: ErrorCode.NETWORK,
      message: 'We could not reach the server. Check your connection and try again.',
      status: 0,
      traceId: '',
      details: [{ field: '', message: String(cause) }],
    })
  }

  if (result.error !== undefined || !result.response.ok) {
    const apiError = toApiError(result.error, result.response)
    reportError(apiError, result.response.url)
    throw apiError
  }

  return result.data as T
}

/* ── Telemetry ───────────────────────────────────────────────────────────
   Every failed request is reported with its trace id, which is what makes a
   frontend failure searchable alongside the backend logs. Reporting must
   never itself throw, and never recurse. */
function reportError(error: ApiError, endpoint: string): void {
  if (endpoint.includes('/v1/telemetry/errors')) return
  // 4xx below 500 are usually expected business outcomes, not incidents.
  if (!error.isServer && !error.isNetwork) return

  /* The backend takes a free-form `context` bag alongside the message, so the
     correlation fields ride there rather than as top-level columns. */
  const payload: components['schemas']['TelemetryError'] = {
    message: error.message,
    url: endpoint,
    user_agent: navigator.userAgent,
    occurred_at: new Date().toISOString(),
    context: {
      trace_id: error.traceId || null,
      status: error.status,
      code: error.code,
      build_id: BUILD_ID,
      route: window.location.pathname,
    } as unknown as Record<string, never>,
  }

  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon?.(`${BASE_URL}/v1/telemetry/errors`, blob)
  } catch {
    /* telemetry is best-effort by definition */
  }
}

export { reportError as reportApiError }
